import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Gift, CheckCircle, XCircle } from 'lucide-react';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PromoRedemption({ user }) {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [promo, setPromo] = useState(null);
  const [error, setError] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    checkPromo();
  }, [code]);

  const checkPromo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/promo/${code}/check`);
      if (response.data.valid) {
        setPromo(response.data);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to load promo code');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!user) {
      // Redirect to login
      toast.info('Please login to redeem this promo code');
      navigate('/');
      return;
    }

    setRedeeming(true);
    try {
      const deviceFp = await getDeviceFingerprint();
      
      // Ensure Authorization header is set
      const token = localStorage.getItem('token');
      const config = token ? {
        headers: { 'Authorization': `Bearer ${token}` }
      } : {};
      
      const response = await axios.post(`${BACKEND_URL}/api/promo/redeem`, {
        promo_code: code,
        device_fingerprint: deviceFp
      }, config);

      setSuccess(true);
      setResult(response.data);
      toast.success(response.data.message);

      // Store discount in localStorage for checkout
      if (response.data.type === 'discount') {
        const discountData = {
          promo_code: response.data.promo_code,
          discount_percentage: response.data.discount_percentage,
          applicable_plans: response.data.applicable_plans,
          redeemed_at: new Date().toISOString()
        };
        localStorage.setItem('active_promo_discount', JSON.stringify(discountData));
      }

      // Redirect based on promo type
      setTimeout(() => {
        if (response.data.type === 'credit') {
          // Credit added - reload user data and go to dashboard
          window.location.href = '/';
        } else if (response.data.redirect_to_checkout && response.data.applicable_plans && response.data.applicable_plans[0] !== 'all') {
          // Specific plan discount - go directly to checkout with plan
          let planId = response.data.applicable_plans[0];
          
          // Map backend plan IDs to frontend plan IDs
          const planIdMapping = {
            'plan_jntu': 'engineering-student-success'
          };
          
          if (planIdMapping[planId]) {
            planId = planIdMapping[planId];
          }
          
          navigate(`/checkout/${planId}`, { 
            state: { 
              promoCode: response.data.promo_code,
              discountPercentage: response.data.discount_percentage
            }
          });
        } else {
          // All plans discount - go to pricing page
          navigate('/pricing');
        }
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to redeem promo code');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading promo code...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Promo Code</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')}>Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="animate-bounce">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">🎉 Success!</h2>
            <p className="text-lg text-gray-700 mb-4">{result?.message}</p>
            
            {result?.type === 'credit' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-green-800 font-semibold text-lg">
                  {result.credits_granted} Credits Added
                </p>
                <p className="text-green-700 text-sm">
                  New Balance: {result.new_balance} Credits
                </p>
              </div>
            )}

            {result?.type === 'discount' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-800 font-semibold text-lg">
                  {result.discount_percentage}% Discount Ready!
                </p>
                <p className="text-blue-700 text-sm">
                  Apply at checkout to get your discount
                </p>
              </div>
            )}

            <p className="text-gray-500 text-sm">Redirecting you now...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4 sm:p-6">
      <Card className="max-w-md w-full">
        <CardContent className="p-4 sm:p-8">
          <div className="text-center mb-6">
            <Gift className="h-12 w-12 sm:h-16 sm:w-16 text-indigo-600 mx-auto mb-4" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Promo Code</h1>
            <div className="inline-block bg-indigo-100 text-indigo-800 px-3 sm:px-4 py-2 rounded-lg text-xl sm:text-2xl font-mono font-bold break-all">
              {code}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-6">
            <h3 className="font-semibold text-sm sm:text-base text-gray-900 mb-2">{promo?.description}</h3>
            
            {promo?.type === 'credit' && (
              <div className="text-center py-3 sm:py-4">
                <div className="text-3xl sm:text-4xl font-bold text-indigo-600 mb-2">
                  {promo.credit_amount}
                </div>
                <div className="text-sm sm:text-base text-gray-600">Free Credits</div>
              </div>
            )}

            {promo?.type === 'discount' && (
              <div className="text-center py-3 sm:py-4">
                <div className="text-3xl sm:text-4xl font-bold text-indigo-600 mb-2">
                  {promo.discount_percentage}%
                </div>
                <div className="text-sm sm:text-base text-gray-600">Discount on Plans</div>
              </div>
            )}
          </div>

          {!user && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 mb-4">
              <p className="text-yellow-800 text-xs sm:text-sm">
                You need to be logged in to redeem this promo code.
              </p>
            </div>
          )}

          <Button 
            className="w-full text-sm sm:text-base py-5 sm:py-6" 
            onClick={handleRedeem}
            disabled={redeeming}
          >
            {redeeming ? 'Redeeming...' : 'Redeem Promo Code'}
          </Button>

          <button
            className="w-full mt-3 text-gray-600 text-sm hover:text-gray-900"
            onClick={() => navigate('/')}
          >
            Back to Home
          </button>
        </CardContent>
      </Card>
    </div>
  );
}

export default PromoRedemption;
